import { ReactNode } from "react";
import {
  Control,
  FieldErrors,
  FieldPath,
  FieldValues,
  UseFormRegister,
} from "react-hook-form";
import { ConnectorWizardFormValues } from "../forms/ConnectorWizard";
import ConnectorRuleSet from "./ConnectorRuleSet";
import { z } from "zod";

export type EditDefaultValuesGetter<
  TFormValues extends FieldValues,
  TConnectorRow extends IConnectorRow = IConnectorRow
> = (connectorRow?: TConnectorRow) => TFormValues;

export interface ConnectorConfigFormProps<
  TFormValues extends FieldValues,
  ConnectorType extends string
> {
  defaultValues: TFormValues;
  register: UseFormRegister<ConnectorWizardFormValues>;
  control: Control<ConnectorWizardFormValues>;
  errors?: FieldErrors<
    ConnectorWizardFormValues & Record<ConnectorType, TFormValues>
  >;
}

export interface IConnectorRow {
  customerId: string;
  connectorId: string;
  jobConfigId: string;
  type: string;
  intervalSeconds: number;
  ruleSets: ConnectorRuleSet[];
  connectorDetails?: Record<string, ConnectorSecret | string | boolean>;
  shouldSaveNoMedia: boolean;
}

export const connectorSecretSchema = ({
  required = false,
  requiredMessage,
}: {
  required?: boolean;
  requiredMessage?: string;
}) => {
  const baseSchema = z.object({
    value: z.string(),
    isSensitive: z.boolean(),
    hasValue: z.boolean(),
  });
  if (required) {
    return baseSchema
      .extend({
        value: z.string(),
      })
      .refine(
        (data) => (data.isSensitive && data.hasValue) || data.value.length > 0,
        {
          message: requiredMessage,
          path: ["value"],
        }
      );
  }
  return baseSchema.nullish();
};

export interface ConnectorSecret {
  value: string;
  isSensitive: boolean;
  hasValue: boolean;
}

export default interface Connector<
  TFormValues extends FieldValues = FieldValues,
  TConnectorRow extends IConnectorRow = IConnectorRow
> {
  get type(): string;
  get defaultValues(): TFormValues;
  get fields(): FieldPath<ConnectorWizardFormValues>[];
  getEditDefaultValues: EditDefaultValuesGetter<TFormValues, TConnectorRow>;
  renderFormFields(
    register: UseFormRegister<ConnectorWizardFormValues>,
    control: Control<ConnectorWizardFormValues>,
    errors?: FieldErrors<ConnectorWizardFormValues>
  ): ReactNode;
}

// Omit connectorDetails if it's empty
export function omitEmptyConnectorDetails(
  connector: IConnectorRow
): IConnectorRow {
  const { connectorDetails, ...rest } = connector;

  if (connectorDetails && Object.keys(connectorDetails).length === 0) {
    return rest as IConnectorRow;
  }

  return connector;
}
